import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Lock, Logout, Refresh, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth, IDLE_LOCK_MINUTES, IDLE_LOGOUT_MINUTES } from '../contexts/AuthContext';
import { useRouter } from '../contexts/RouterContext';

// Páginas deslogadas: o bloqueio não pode aparecer por cima delas (achado do Hilton, 07/09/2026). Se a
// sessão travada sobrou de uma aba anterior, encerramos em silêncio em vez de pedir senha na tela de login.
const PUBLIC_PAGES = new Set<string>(['login', 'landing', 'alias-register', 'cadastro', 'set-password', 'checkin', 'patient-intake']);

// RN-006: bloqueio por inatividade. Fica POR CIMA da página (nada se perde): a senha renova o
// token e reabre a tela; "Sair" encerra a sessão. Sem resposta até o limite de logout, o
// contexto encerra sozinho e volta ao login.
const SessionLockDialog: React.FC = () => {
  const { user, isAuthenticated, isLocked, renewSession, logout } = useAuth();
  const { currentPage } = useRouter();
  const onPublicPage = PUBLIC_PAGES.has(currentPage);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isLocked) {
      setPassword('');
      setError('');
      setShowPassword(false);
    }
  }, [isLocked]);

  useEffect(() => {
    if (isLocked && isAuthenticated && onPublicPage) {
      // sem redirecionar: quem está num formulário público (cadastro, check-in) não pode ser jogado no login
      logout(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocked, isAuthenticated, onPublicPage]);

  const handleRenew = async () => {
    if (!password.trim()) {
      setError('Digite sua senha para continuar.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const result = await renewSession(password);
      if (!result.success) {
        setError(result.error || 'Não foi possível renovar a sessão.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated || !isLocked || onPublicPage) {
    return null;
  }

  return (
    <Dialog
      open
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', border: '3px solid #03B4C6' } }}
    >
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Lock sx={{ fontSize: '3rem', color: '#03B4C6', mb: 1 }} />
          <Typography variant="h6" sx={{ color: '#2D3748', fontWeight: 600, mb: 0.5 }}>
            Sessão bloqueada por inatividade
          </Typography>
          <Typography variant="body2" sx={{ color: '#6c757d' }}>
            Sem atividade há {IDLE_LOCK_MINUTES} minutos. Digite sua senha para continuar de onde parou —
            sem resposta em {IDLE_LOGOUT_MINUTES / 60} horas, a sessão é encerrada.
          </Typography>
        </Box>
        <TextField
          label="E-mail"
          type="email"
          value={user?.email || ''}
          disabled
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{ readOnly: true, sx: { backgroundColor: '#f8f9fa', color: '#6c757d' } }}
        />
        {/* new-password: o navegador não oferece a senha salva no bloqueio — em computador
            compartilhado, um clique no preenchimento destravaria a sessão de outra pessoa */}
        <TextField
          label="Senha"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (error) setError('');
          }}
          placeholder="Digite sua senha para continuar"
          autoComplete="new-password"
          autoFocus
          fullWidth
          error={!!error}
          helperText={error}
          disabled={submitting}
          inputProps={{ maxLength: 100 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleRenew();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" tabIndex={-1}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3, flexWrap: 'wrap' }}>
          <Button
            onClick={() => logout(true)}
            variant="outlined"
            color="error"
            startIcon={<Logout />}
            sx={{ textTransform: 'none' }}
          >
            Sair
          </Button>
          <Button
            onClick={handleRenew}
            variant="contained"
            disabled={submitting}
            startIcon={<Refresh />}
            sx={{ textTransform: 'none', backgroundColor: '#03B4C6', '&:hover': { backgroundColor: '#029AAB' } }}
          >
            Renovar
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SessionLockDialog;
